import { z } from 'zod';
import { uuidSchema } from './common.js';
import { bankValidationStatusSchema } from './suppliers.js';
/**
 * Supplier Portal (BP-02 §2.4, D-076 prototype, D-102 supplier-maintained master data) — the core
 * loop only. A buyer invites a supplier contact by email; the supplier opens a no-login org-branded
 * form (token in the URL), edits its own company-profile fields, and submits. Each field lands as
 * "supplier-provided, unreviewed" until a buyer user accepts or rejects it. Shapes shared by the API
 * (validation) and the web app (types). Deferred: file uploads, the bank/payment dual-authorization
 * (D-102), and the supplier self-service login.
 */
/** The company-profile fields a supplier may maintain in v1 (a subset of the supplier master). */
export const portalFieldKeySchema = z.enum([
    'name', 'legalName', 'country', 'vatNumber', 'lei', 'duns', 'address', 'contactName', 'contactEmail', 'contactPhone',
]);
export const portalInviteStatusSchema = z.enum(['sent', 'opened', 'in_progress', 'submitted', 'accepted']);
export const portalReviewStatusSchema = z.enum(['unreviewed', 'accepted', 'rejected']);
/* ----------------------------------------------------------------- buyer side (authenticated) */
/** POST /master-data/suppliers/:id/portal-invites — invite a contact (org_admin). Emails a link. */
export const createPortalInviteSchema = z
    .object({
    contactName: z.string().trim().min(1).max(120),
    contactEmail: z.string().trim().email().max(200),
    contactRole: z.string().trim().max(60).optional(),
    templateKey: z.string().trim().max(60).optional(),
    /** D-158: attach a contract template so one magic link co-fills the contract + master data. */
    contract: z
        .object({
        familyId: uuidSchema,
        version: z.number().int().min(1),
        supplierTokens: z.array(z.string().trim().min(1).max(60)).max(40),
    })
        .strict()
        .optional(),
})
    .strict();
/** An invite as the buyer sees it in the supplier's Portal tab (with a count of fields awaiting review). */
export const portalInviteSchema = z.object({
    id: uuidSchema,
    supplierId: uuidSchema,
    contactId: uuidSchema,
    contactName: z.string(),
    contactEmail: z.string(),
    templateKey: z.string(),
    status: portalInviteStatusSchema,
    expiresAt: z.string(),
    submittedAt: z.string().nullable(),
    lastSentAt: z.string(),
    createdAt: z.string(),
    pendingReviewCount: z.number(),
    erasureRequested: z.boolean(),
    /** BP-22: the notice version + time this invite accepted, so the buyer can open the exact text. */
    consentVersion: z.string().nullable(),
    consentAcceptedAt: z.string().nullable(),
    /** D-158: true when the invite carries a contract to co-fill. */
    hasContract: z.boolean().default(false),
});
/** POST /portal/login — the supplier requests a passwordless sign-in link by email (D-102/D-136). */
export const portalLoginSchema = z.object({ email: z.string().trim().email().max(200) }).strict();
/** A supplier-provided field a buyer user reviews (accept writes it to the master, audited). */
export const portalSubmissionSchema = z.object({
    id: uuidSchema,
    inviteId: uuidSchema,
    supplierId: uuidSchema,
    fieldKey: portalFieldKeySchema,
    fieldLabel: z.string(),
    value: z.string().nullable(),
    currentValue: z.string().nullable(),
    reviewStatus: portalReviewStatusSchema,
    reviewedAt: z.string().nullable(),
});
/** PATCH /portal-submissions/:id — a buyer user accepts or rejects one supplier-provided field. */
export const reviewSubmissionSchema = z
    .object({ reviewStatus: z.enum(['accepted', 'rejected']) })
    .strict();
/* --------------------------------------------------------------- supplier side (public, token) */
/** One field on the public form, prefilled from the buyer's record or the supplier's saved progress. */
export const portalFormFieldSchema = z.object({
    key: portalFieldKeySchema,
    label: z.string(),
    value: z.string().nullable(),
    fromRecord: z.boolean(),
});
/** GET /portal/:token — the form schema + current state the supplier sees (no app data leaks). */
export const portalFormSchema = z.object({
    status: portalInviteStatusSchema,
    orgName: z.string(),
    supplierName: z.string(),
    contactName: z.string(),
    expiresAt: z.string(),
    consentAccepted: z.boolean(),
    consentVersion: z.string(),
    /** BP-22: the consent gate renders the notice text from the legal-documents store, not from code. */
    consentTitle: z.string(),
    consentBodyMd: z.string(),
    fields: z.array(portalFormFieldSchema),
    /** D-158: present only when the invite carries a contract template to co-fill. */
    contractName: z.string().nullable().default(null),
    contractTokens: z
        .array(z.object({ key: z.string(), label: z.string(), value: z.string().nullable() }))
        .default([]),
});
/** POST /portal/:token/consent — the supplier accepts the privacy + terms notice before any save. */
export const acceptConsentSchema = z.object({ version: z.string().trim().min(1).max(60) }).strict();
/* ------------------------------------- bank-info validation (D-141, supersedes D-139) ----------- */
// The supplier's bank fields (IBAN, BIC, bank name, account holder) live on the supplier record and
// always show the latest entered values plus a validation status. The supplier appoints its own finance
// colleague by email; three parties (supplier contact, supplier finance, buyer) may change the values;
// any change flips the status to "not validated" and emails finance a validation link; finance-only
// validation flips it to "validated". Payment terms are buyer-only (see patchSupplierSchema).
/** POST /portal/:token/bank-info — the supplier contact enters bank info and appoints finance by email. */
export const portalBankInfoSchema = z
    .object({
    iban: z.string().trim().max(40).optional(),
    bic: z.string().trim().max(20).optional(),
    bankName: z.string().trim().max(160).optional(),
    accountHolder: z.string().trim().max(200).optional(),
    financeEmail: z.string().trim().email().max(200),
})
    .strict();
/** GET /portal/:token/bank — the bank section the supplier contact sees on the portal. */
export const portalBankViewSchema = z.object({
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    bankName: z.string().nullable(),
    accountHolder: z.string().nullable(),
    financeEmail: z.string().nullable(),
    validationStatus: bankValidationStatusSchema,
});
/** GET /portal/bank-validate/:token — what the supplier's finance colleague sees on their link. */
export const financeValidatePageSchema = z.object({
    orgName: z.string(),
    supplierName: z.string(),
    financeEmail: z.string(),
    validationStatus: bankValidationStatusSchema,
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    bankName: z.string().nullable(),
    accountHolder: z.string().nullable(),
});
/** PUT /portal/bank-validate/:token — finance edits the values (which then need validating again). */
export const financeEditBankSchema = z
    .object({
    iban: z.string().trim().max(40).optional(),
    bic: z.string().trim().max(20).optional(),
    bankName: z.string().trim().max(160).optional(),
    accountHolder: z.string().trim().max(200).optional(),
})
    .strict();
/** PUT /portal/:token/fields — autosave the supplier's edits (resumes on link re-entry). */
export const savePortalFieldsSchema = z
    .object({
    fields: z
        .array(z.object({ key: portalFieldKeySchema, value: z.string().trim().max(200) }))
        .max(20),
    /** D-158: supplier answers to the buyer-curated contract tokens (name = token, no 'token:' prefix). */
    tokens: z
        .array(z.object({ name: z.string().trim().min(1).max(60), value: z.string().trim().max(2000) }))
        .max(40)
        .optional(),
})
    .strict();
/** POST /portal/:token/submit — the supplier finalises; org is notified, fields await review. */
export const portalSubmitResultSchema = z.object({ status: portalInviteStatusSchema, submitted: z.number() });
/* -------------------------------------------------- contract co-fill (D-158, buyer side) -------- */
/** GET /portal-invites/:id/cofill — the co-filled contract the buyer reviews: the snapshotted render
 *  with each supplier token replaced by its ACCEPTED value (unaccepted tokens stay open). */
export const contractCoFillTokenSchema = z.object({
    name: z.string(),
    label: z.string(),
    value: z.string().nullable(),
    reviewStatus: portalReviewStatusSchema,
});
export const contractCoFillSchema = z.object({
    inviteId: uuidSchema,
    contractName: z.string(),
    contractVersion: z.number().int(),
    status: portalInviteStatusSchema,
    bodyMd: z.string(),
    tokens: z.array(contractCoFillTokenSchema),
});
/** POST /portal-invites/:id/tokens/review — a buyer accepts/rejects one supplier token value. */
export const reviewCoFillTokenSchema = z
    .object({ token: z.string().trim().min(1).max(60), reviewStatus: z.enum(['accepted', 'rejected']) })
    .strict();
//# sourceMappingURL=portal.js.map